"use client";

import React from "react";
import {
  Dialog as BaseDialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter as BaseDialogFooter,
  DialogClose,
} from "./dialog";
import { createPortal } from "react-dom";
import { Button } from "./button";
import { X } from "lucide-react";

export const DialogPortalContext = React.createContext<HTMLElement | null>(null);
export const DialogFooterPortalContext = React.createContext<HTMLDivElement | null>(null);

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  showCloseButton?: boolean;
  maxHeightClass?: string;
  contentClassName?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "lg",
  showCloseButton = true,
  maxHeightClass = "max-h-[90vh]",
  contentClassName = "px-4 py-4 sm:px-6",
}: DialogProps) {
  // Mobile-first: leave a comfortable horizontal inset so the dialog never
  // hugs the viewport edge. `100vw-3rem` = 24px gutter on each side at every
  // breakpoint (the gutter is mirrored at `sm+` once the max-width caps in,
  // because Radix centers the dialog within the remaining space).
  const maxWidths: Record<NonNullable<DialogProps["maxWidth"]>, string> = {
    sm: "max-w-[calc(100vw-3rem)] sm:max-w-sm",
    md: "max-w-[calc(100vw-3rem)] sm:max-w-md",
    lg: "max-w-[calc(100vw-3rem)] sm:max-w-lg",
    xl: "max-w-[calc(100vw-3rem)] sm:max-w-xl",
    "2xl": "max-w-[calc(100vw-3rem)] sm:max-w-2xl",
    "3xl": "max-w-[calc(100vw-3rem)] sm:max-w-6xl",
    "4xl": "max-w-[calc(100vw-3rem)] sm:max-w-4xl",
  };

  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  const [footerTarget, setFooterTarget] = React.useState<HTMLDivElement | null>(null);

  return (
    <BaseDialog open={ open } onOpenChange={ (v) => !v && onClose() }>
      <DialogContent
        aria-describedby={ undefined }
        className={ `w-full ${maxWidths[maxWidth]} ${maxHeightClass} flex flex-col p-0 overflow-hidden [&>button]:hidden` }
        onEscapeKeyDown={ onClose as any }
        onPointerDownOutside={ (e) => {
          if ((e.target as Element)?.closest?.('[data-radix-popper-content-wrapper]')) return;
          e.preventDefault();
        } }
      >
        { (title || subtitle || showCloseButton) && (
          <DialogHeader className="flex flex-row items-center justify-between text-left border-b border-border px-4 py-3 sm:px-6 sm:py-4 flex-shrink-0 rounded-t-lg">
            <div className="flex-1 min-w-0 text-left">
              { title && <DialogTitle className="text-xl font-semibold text-foreground text-left">{ title }</DialogTitle> }
              { subtitle && <p className="mt-1 text-sm text-muted-foreground text-left">{ subtitle }</p> }
            </div>
            { showCloseButton && (
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </Button>
              </DialogClose>
            ) }
          </DialogHeader>
        ) }

        <div className="flex min-h-0 flex-1 flex-col" ref={ setPortalContainer as React.LegacyRef<HTMLDivElement> }>
          <DialogPortalContext.Provider value={ portalContainer }>
            <DialogFooterPortalContext.Provider value={ footerTarget }>
              <div className={ `flex-1 overflow-y-auto ${contentClassName}` }>{ children }</div>
              <div ref={ setFooterTarget } className="flex-shrink-0" />
            </DialogFooterPortalContext.Provider>
          </DialogPortalContext.Provider>
        </div>
      </DialogContent>
    </BaseDialog>
  );
}

export interface DialogFooterProps {
  children: React.ReactNode;
}

export function DialogFooter({ children }: DialogFooterProps) {
  const footerTarget = React.useContext(DialogFooterPortalContext);

  const content = (
    <BaseDialogFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 py-3 sm:px-6 sm:py-4 border-t border-border bg-card rounded-b-lg">
      { children }
    </BaseDialogFooter>
  );

  if (footerTarget) {
    return createPortal(content, footerTarget);
  }

  return content;
}
